import React, { useState, useEffect, useRef } from "react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import StarIcon from "@mui/icons-material/Star";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import "./MyTechProfileProjectComponent.css";
import AnimatedGradientText from "./AnimatedGradient";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";

const colors = [
  "maroon",
  "darkgreen",
  "violet",
  "purple",
  "darkblue",
  "#B8860B",
];

const ProjectDetails = ({ project, color }) => {
  return (
    <>
      <h2 className="mytechprofileproject-details-title">{project?.title}</h2>
      <p className="mytechprofileproject-details-desc">{project?.details}</p>
      <ul className="mytechprofileproject-highlights">
        {project?.highlights?.map((highlight, i) => (
          <li key={i}>
            <StarIcon style={{ color }} />
            <span>{highlight?.title}</span>
          </li>
        ))}
      </ul>
      <div>
        <GithubButton
          onClick={() =>
            window.open(project?.githubLink, "_blank", "noopener,noreferrer")
          }
        />
      </div>

      <div className="mytechprofileproject-tech-section">
        <h4 className="mytechprofileproject-tech-title">Technologies Used</h4>
        <div className="mytechprofileproject-tech-list">
          {project?.techStack?.map((tech, i) => (
            <div key={i} className="mytechprofileproject-tech-item">
              <img src={tech?.icon} alt={`${tech?.name} logo`} />
              <span>{tech?.name}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const GithubButton = ({ onClick }) => {
  const iconContainerRef = useRef(null);
  const [iconColor, setIconColor] = useState(true);
  const handleMouseEnter = () => {
    if (iconContainerRef.current) {
      setIconColor(false);
    }
  };

  // Event handler for when the mouse leaves the button
  const handleMouseLeave = () => {
    if (iconContainerRef.current) {
      setIconColor(true);
    }
  };
  return (
    <motion.button
      className="mytechprofileproject-glassy-button"
      whileHover="hover"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
    >
      <>
        <div
          className="mytechprofileproject-button-icon"
          ref={iconContainerRef}
          style={{ zIndex: "1", marginLeft: "5px" }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24" // Or your desired size
            height="24" // Or your desired size
            viewBox="0 0 24 24"
            fill={iconColor ? "#fff" : "black"} // Dynamically sets the color
            style={{ zIndex: "10" }}
          >
            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.91 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
          </svg>
        </div>
        <motion.span
          variants={{ hover: { color: "#222" } }}
          className="mytechprofileproject-button-text"
        >
          View Github
        </motion.span>
      </>

      <motion.span
        className="mytechprofileproject-arrow-icon-bg"
        variants={{ hover: { x: 5 } }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <ArrowForwardIcon />
      </motion.span>

      <motion.div
        className="mytechprofileproject-button-hover-bg"
        variants={{ hover: { scaleX: 1, originX: 0 } }}
        initial={{ scaleX: 0 }}
        transition={{ duration: 0.4, ease: [0.43, 0.13, 0.23, 0.96] }}
      />
    </motion.button>
  );
};

const MyTechProfileProjectComponent = ({ projectsRef }) => {
  const { profile } = useSelector((state) => state.editMyTechProfileStore);
  const [activeProjectIndex, setActiveProjectIndex] = useState(0);
  const projectRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index, 10);
            setActiveProjectIndex(index);
          }
        });
      },
      {
        rootMargin: "-50% 0px -50% 0px",
        threshold: 0,
      }
    );

    const currentRefs = projectRefs.current;
    currentRefs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      currentRefs.forEach((ref) => {
        if (ref) observer.disconnect();
      });
    };
  }, [profile?.projects]);

  const getGradient = (color) =>
    `linear-gradient(135deg, ${color} 0%, #000000 100%)`;

  return (
    <div className="mytechprofileproject-container">
      <div className="mytechprofileproject-header">
        <h3 ref={projectsRef} className="mytechprofileproject-subtitle">
          Featured Case Studies
        </h3>
        <h1 className="mytechprofileproject-title">
          Curated <AnimatedGradientText text="Work" />
        </h1>
      </div>

      <div className="mytechprofileproject-main-section">
        {/* Left Column (contains cards and hidden mobile details) */}
        <div className="mytechprofileproject-left">
          {profile?.projects?.map((project, index) => (
            <div
              key={project?.id}
              className="mytechprofileproject-project-item-container"
            >
              {/* Project Image Card */}
              <div
                ref={(el) => (projectRefs.current[index] = el)}
                data-index={index}
                className="mytechprofileproject-project-card"
                style={{
                  background: getGradient(colors[index % colors.length]),
                  cursor: "pointer",
                }}
                onClick={() =>
                  window.open(
                    project?.githubLink,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                <div className="mytechprofileproject-card-header">
                  <h2>{project?.title}</h2>
                  <ArrowForwardIosIcon />
                </div>
                <div className="mytechprofileproject-image-wrapper">
                  <img
                    src={project?.image}
                    alt={project?.title}
                    className="mytechprofileproject-project-image"
                  />
                </div>
              </div>

              {/* Mobile-Only Details Section */}
              <div className="mytechprofileproject-mobile-details">
                <ProjectDetails
                  project={project}
                  color={colors[index % colors.length]}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Right Sticky Column (Desktop-Only) */}
        <div className="mytechprofileproject-right">
          <div className="mytechprofileproject-details-content">
            <ProjectDetails
              project={profile?.projects[activeProjectIndex]}
              color={colors[activeProjectIndex % colors.length]}
            />
          </div>
        </div>
      </div>

      {/* <div className="mytechprofileproject-see-more-container">
        <button className="mytechprofileproject-see-more-btn">
          See More Projects
          <ArrowForwardIcon fontSize="small" />
        </button>
      </div> */}
    </div>
  );
};

export default MyTechProfileProjectComponent;
